import SalesLeadDeleteDialog from "./SalesLeadDeleteDialog";
import {
	Crudable,
	FetchesData
} from "kinnect-mixins";
import { SalesLead } from "kinnect-models";
import {
	KAddressMapDialog,
	KDataTable,
	KDataTableMenu,
	KPhoneLink,
	KTimeAgo
} from "kinnect-general";
import {
	VBtn,
	VCard,
	VCol,
	VContainer,
	VIcon,
	VRow,
	VStepper,
	VStepperContent,
	VStepperStep,
	VSubheader
} from "vuetify/lib";
import {
	formatDate,
	formatTime
} from "kinnect-formatters";
import { removeByObjectValue } from "kinnect-helpers";

export default KDataTable.extend( {
	name: "SalesLeadsTable",

	mixins: [
		Crudable,
		FetchesData
	],

	props: {
		itemsFilter: {
			default: item => item,
			type: Function
		},

		modifyItemsQuery: {
			default: query => query,
			type: Function
		},

		noDataText: {
			...KDataTable.options.props.noDataText,
			default: "There are no sales leads to display"
		},

		noResultsText: {
			...KDataTable.options.props.noResultsText,
			default: "No matching sales leads found"
		},

		sortBy: {
			...KDataTable.options.props.sortBy,
			default: "age"
		}
	},

	data: () => ( {
		items: [],
		crud: "sales_leads",
		statusStepNum: {
			"Active": 1,
			"Completed": 2
		}
	} ),

	computed: {
		filteredItems() {
			return this.items.filter( this.itemsFilter );
		},

		defaultHeaders() {
			return [
				{
					text: "Age",
					value: "age"
				},
				{
					text: "Company",
					value: "companyName"
				},
				{
					text: "Contact",
					value: "contactName"
				},
				{
					text: "Project Address",
					value: "projectFullAddress"
				},
				{
					text: "Phone",
					value: "phone"
				},
				{
					text: "Email",
					value: "email"
				},
				{
					text: "Notes",
					value: "notes"
				},
				{
					align: "end",
					sortable: false,
					text: this.isMobile ? "Actions" : "",
					value: "actions"
				}
			];
		}
	},

	methods: {
		genStatusTime( item, status ) {
			const timestamp = this.getStatusTime( item, status );

			if ( timestamp ) {
				return <span class="text-caption text-no-wrap">
					({formatDate( timestamp )} at {formatTime( timestamp )})
				</span>;
			}
		},

		getStatusTime( item, statusName ) {
			const matching = item.statuses.find( status => status.name === statusName );

			if ( matching ) {
				return matching.created_at;
			}
		},

		genItemStatusStepper( item ) {
			return <VStepper value={this.statusStepNum[item.$.status]} vertical>
				{!item.completed && <VSubheader>Mark Sales Lead As</VSubheader>}

				<VStepperStep
					step="1"
					complete={this.statusStepNum[item.$.status] > 1}
				>
					<span>Added</span>
					{this.genStatusTime( item, "Active" )}
				</VStepperStep>

				<VStepperContent step="1">
					<VBtn
						color="primary"
						depressed
						loading={this.$wait.is( "save sales lead" )}
						onClick={() => this.updateStatus( item, "Completed" )}
					>Mark as Completed</VBtn>
				</VStepperContent>

				<VStepperStep
					complete={this.statusStepNum[item.$.status] > 1}
					step="2"
				>
					<span>Completed</span>
					{this.genStatusTime( item, "Completed" )}
				</VStepperStep>
			</VStepper>;
		},

		genItemActions( item ) {
			return <KDataTableMenu closeOnContentClick={false}>
				<VCard dark>
					<VContainer fluid>
						<VRow>
							{this.canEdit && <VCol>
								{this.genItemStatusStepper( item )}
							</VCol>}

							<VCol>
								<VBtn
									block
									class="btn--text-left mb-2"
									depressed
									large
									to={{
										name: "salesLead",
										params: { idSalesLead: item.id }
									}}
								><VIcon left>visibility</VIcon> View</VBtn>

								{this.canDelete
									&& !item.completed
									&& <SalesLeadDeleteDialog
										item={item}
										onConfirm={() => this.deleteItem( item )}
										scopedSlots={{
											activator: ( { on } ) =>
												<VBtn
													block
													class="btn--text-left"
													depressed
													large
													on={on}
												>
													<VIcon left>delete</VIcon> Delete
												</VBtn>
										}}
									></SalesLeadDeleteDialog>
								}
							</VCol>
						</VRow>
					</VContainer>
				</VCard>
			</KDataTableMenu>;
		},

		updateStatus( item, status ) {
			item.status = status;

			return item.save();
		},

		deleteItem( item ) {
			return item.delete().then( () => {
				this.items = removeByObjectValue( this.items, "id", item.id );
			} );
		},

		async fetchItems() {
			this.items = await this.modifyItemsQuery(
				SalesLead
				.select( {
					statuses: [ "created_at", "model_id", "name" ]
				} )
				.include( [ "statuses" ] )
			)
			.get();
		},

		async fetch() {
			await this.fetchItems();
		}
	},

	render() {
		return <KDataTable
			attrs={this.$props}
			headers={this.$_headers}
			items={this.filteredItems}
			loading={this.$wait.is( "get sales leads" )}
			on={{ ...this.$listeners }}
			scopedSlots={{
				"item.age": ( { item } ) =>
					<KTimeAgo
						includeSeconds={false}
						startTime={item.createdTimestamp}
					></KTimeAgo>,

				"item.projectFullAddress": ( { item } ) => (
					item.projectAddress && <KAddressMapDialog address={{
						address: item.projectAddress,
						city: item.projectCity,
						lat: item.projectLat,
						lng: item.projectLng
					}}></KAddressMapDialog>
				),

				"item.phone": ( { item } ) => (
					item.phone && <KPhoneLink phone={item.phone}></KPhoneLink>
				),

				"item.actions": ( { item } ) => this.genItemActions( item ),

				...this.$scopedSlots
			}}
		></KDataTable>;
	},

	beforeCreate() {
		delete this.$options.props.items;
		delete this.$options.props.loading;
	}
} );
